import { useCallback, useEffect, useRef, useState, type KeyboardEvent as ReactKeyboardEvent } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  SESSION_RECORDING_CAP_WARNING_SECONDS,
  SESSION_RECORDING_MAX_SECONDS,
  type SessionRecordingWithdrawnBy,
} from '@shared/sessions/session-recording'
import type { RecorderInterruptionReason } from '../data/recorder-port'
import {
  SILENCE_WARNING_MS,
  discardActiveRecording,
  finishActiveRecording,
  isCaptureLive,
  liveElapsedSec,
  pauseActiveRecording,
  resumeActiveRecording,
  useActiveSessionRecording,
  useRecorderLevel,
  withdrawActiveRecording,
  type FinishResult,
} from '../hooks/use-active-session-recording'
import { formatRecordingClock, formatRecordingMinutesSpoken } from '../utils/recording-format'
import { DiscardRecordingDialog, SomeoneJoinedDialog, WithdrawConsentDialog } from './rec-bar-dialogs'
import type { ToastTone } from '../../../components/toast'

const INTERRUPTION_LABELS: Record<RecorderInterruptionReason, string> = {
  phone_call: 'phone call',
  ascend_call: 'Ascend call',
  system_interruption: 'interrupted by the phone',
  route_change: 'audio device changed',
  low_storage: 'phone storage is low',
  mic_lost: 'microphone unavailable',
}

export type RecBarNotify = (message: string, tone?: ToastTone) => void

export function finishToast(result: FinishResult): { message: string; tone: ToastTone } {
  return result.uploadQueued
    ? { message: 'Saved on this phone — uploading', tone: 'success' }
    : {
        message: 'Saved on this phone. The upload hasn’t started — open the session to upload it.',
        tone: 'error',
      }
}

function LevelMeter({ active }: { active: boolean }) {
  const { level } = useRecorderLevel()
  const bars = [0.08, 0.2, 0.38, 0.58, 0.8]
  return (
    <span className="flex h-5 items-end gap-0.5" aria-hidden data-testid="rec-level-meter">
      {bars.map((threshold, index) => {
        const lit = active && level >= threshold
        return (
          <span
            key={threshold}
            className={`w-1 rounded-sm motion-safe:transition-colors ${lit ? 'bg-emerald-400' : 'bg-white/25'}`}
            style={{ height: `${30 + index * 17}%` }}
          />
        )
      })}
    </span>
  )
}

type Dialog = 'someone_joined' | 'withdraw' | 'discard' | null

const barButton =
  'a11y-touch-target inline-flex shrink-0 items-center justify-center rounded-full px-3 text-sm font-semibold focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-slate-900 disabled:opacity-50'

/**
 * Global REC bar (mounted once in the app chrome): visible on every screen
 * while a capture is live — red dot + "REC", count-up timer, mic meter,
 * Pause / Resume, Finish and an overflow (Someone joined, Stop — consent
 * withdrawn, Discard). Not a modal; the rest of the app stays usable.
 */
export function RecBar({ notify }: { notify: RecBarNotify }) {
  const recording = useActiveSessionRecording()
  const { lastAudibleAtMs } = useRecorderLevel()
  const navigate = useNavigate()
  const [now, setNow] = useState(() => Date.now())
  const [menuOpen, setMenuOpen] = useState(false)
  const [dialog, setDialog] = useState<Dialog>(null)
  const [withdrawPreset, setWithdrawPreset] = useState<SessionRecordingWithdrawnBy | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)
  const menuButtonRef = useRef<HTMLButtonElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)

  const { snapshot, ref, busy } = recording
  const live = isCaptureLive(snapshot)
  const stoppedHere = snapshot.state === 'stopped' && ref != null && busy == null
  const visible = live || stoppedHere || busy === 'finishing'

  useEffect(() => {
    if (!visible) return
    setNow(Date.now())
    const id = window.setInterval(() => setNow(Date.now()), 1000)
    return () => window.clearInterval(id)
  }, [visible])

  useEffect(() => {
    if (!visible) {
      setMenuOpen(false)
      setDialog(null)
      setActionError(null)
    }
  }, [visible])

  // Focus the first menu item on open; Esc / outside tap closes.
  useEffect(() => {
    if (!menuOpen) return
    menuRef.current?.querySelector<HTMLElement>('[role="menuitem"]')?.focus()
    const onPointer = (event: PointerEvent) => {
      if (
        !menuRef.current?.contains(event.target as Node) &&
        !menuButtonRef.current?.contains(event.target as Node)
      ) {
        setMenuOpen(false)
      }
    }
    document.addEventListener('pointerdown', onPointer)
    return () => document.removeEventListener('pointerdown', onPointer)
  }, [menuOpen])

  const run = useCallback(
    async (action: () => Promise<void>, failure: string) => {
      setActionError(null)
      try {
        await action()
      } catch {
        setActionError(failure)
      }
    },
    [],
  )

  const handlePause = () => void run(() => pauseActiveRecording('private_moment'), 'Couldn’t pause — try again.')

  const handleResume = () => {
    if (snapshot.pauseReason === 'someone_joined') {
      setDialog('someone_joined')
      return
    }
    void run(() => resumeActiveRecording(), 'Couldn’t resume — try again.')
  }

  const handleFinish = () =>
    void run(async () => {
      const result = await finishActiveRecording({ cause: 'finished' })
      const toast = finishToast(result)
      notify(toast.message, toast.tone)
    }, 'Couldn’t finish the recording — try again.')

  const handleSomeoneJoined = () => {
    setMenuOpen(false)
    if (snapshot.state === 'recording') {
      void run(() => pauseActiveRecording('someone_joined'), 'Couldn’t pause — try again.')
    }
    setDialog('someone_joined')
  }

  const handleWithdraw = (by: SessionRecordingWithdrawnBy) =>
    void run(async () => {
      const result = await withdrawActiveRecording(by)
      setDialog(null)
      notify(
        result.serverConfirmed
          ? 'Recording stopped and deleted'
          : 'Recording stopped and deleted from this phone — we’ll finish deleting it when you’re back online',
      )
    }, 'Couldn’t stop the recording — try again.')

  const handleDiscard = () =>
    void run(async () => {
      await discardActiveRecording()
      setDialog(null)
      notify('Recording discarded')
    }, 'Couldn’t discard the recording — try again.')

  const onMenuKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    const items = Array.from(menuRef.current?.querySelectorAll<HTMLElement>('[role="menuitem"]') ?? [])
    const index = items.indexOf(document.activeElement as HTMLElement)
    if (event.key === 'Escape') {
      event.preventDefault()
      setMenuOpen(false)
      menuButtonRef.current?.focus()
    } else if (event.key === 'ArrowDown') {
      event.preventDefault()
      items[(index + 1) % items.length]?.focus()
    } else if (event.key === 'ArrowUp') {
      event.preventDefault()
      items[(index - 1 + items.length) % items.length]?.focus()
    } else if (event.key === 'Tab') {
      setMenuOpen(false)
    }
  }

  if (!visible) return null

  const elapsed = liveElapsedSec(recording, now)
  const paused = snapshot.state === 'paused'
  const linked = ref != null
  const sessionUuid = ref?.sessionUuid ?? null
  const remainingSec = SESSION_RECORDING_MAX_SECONDS - elapsed
  const capWarning =
    live && remainingSec <= SESSION_RECORDING_CAP_WARNING_SECONDS && remainingSec > 0
  const silent =
    snapshot.state === 'recording' && lastAudibleAtMs > 0 && now - lastAudibleAtMs >= SILENCE_WARNING_MS

  const statusText = stoppedHere
    ? `Recording stopped at ${formatRecordingClock(elapsed)}`
    : busy === 'finishing'
      ? 'Saving the recording…'
      : paused
        ? snapshot.interruption
          ? `Paused — ${INTERRUPTION_LABELS[snapshot.interruption]}`
          : snapshot.pauseReason === 'someone_joined'
            ? 'Paused — someone joined'
            : 'Paused — private moment'
        : snapshot.state === 'starting'
          ? 'Starting…'
          : 'Recording'

  const disabled = busy != null

  return (
    <>
      <section
        aria-label="Session recording"
        data-testid="rec-bar"
        className="pointer-events-auto relative w-full max-w-xl rounded-2xl bg-slate-900/95 px-3 py-2 text-white shadow-xl ring-1 ring-white/10 backdrop-blur"
      >
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => sessionUuid && navigate(`/sessions?session=${encodeURIComponent(sessionUuid)}`)}
            disabled={!sessionUuid}
            aria-label={`${statusText}, ${formatRecordingClock(elapsed)}. Open the recorded session.`}
            className="a11y-touch-target flex min-w-0 flex-1 items-center gap-2 rounded-xl px-1 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
          >
            <span
              className={`h-3 w-3 shrink-0 rounded-full ${paused || stoppedHere ? 'bg-amber-400' : 'bg-red-500 motion-safe:animate-pulse'}`}
              aria-hidden
            />
            <span className="shrink-0 text-xs font-bold tracking-widest text-red-300" aria-hidden>
              {paused ? 'PAUSED' : stoppedHere ? 'STOPPED' : 'REC'}
            </span>
            <span className="shrink-0 font-mono text-base font-semibold tabular-nums" aria-hidden>
              {formatRecordingClock(elapsed)}
            </span>
            <LevelMeter active={snapshot.state === 'recording'} />
            <span className="min-w-0 truncate text-xs text-slate-300 max-sm:hidden" aria-hidden>
              {statusText}
            </span>
          </button>

          {stoppedHere ? (
            <>
              <button
                type="button"
                onClick={handleFinish}
                disabled={disabled}
                className={`${barButton} bg-emerald-500 text-white hover:bg-emerald-400`}
              >
                Finish & create note
              </button>
              <button
                type="button"
                onClick={() => setDialog('discard')}
                disabled={disabled}
                className={`${barButton} bg-white/10 text-white hover:bg-white/20`}
              >
                Discard
              </button>
            </>
          ) : (
            <>
              {paused ? (
                <button
                  type="button"
                  onClick={handleResume}
                  disabled={disabled}
                  aria-label={snapshot.interruption ? 'Resume recording' : 'Resume'}
                  className={`${barButton} bg-white text-slate-900 hover:bg-slate-100`}
                >
                  {snapshot.interruption ? 'Resume recording' : 'Resume'}
                </button>
              ) : (
                <button
                  type="button"
                  onClick={handlePause}
                  disabled={disabled || snapshot.state !== 'recording'}
                  aria-label="Pause recording for a private moment"
                  className={`${barButton} bg-white/10 text-white hover:bg-white/20`}
                >
                  Pause
                </button>
              )}
              {linked && (
                <button
                  type="button"
                  onClick={handleFinish}
                  disabled={disabled}
                  aria-label="Finish recording and create the note"
                  className={`${barButton} bg-emerald-500 text-white hover:bg-emerald-400`}
                >
                  {busy === 'finishing' ? 'Saving…' : 'Finish'}
                </button>
              )}
              <button
                ref={menuButtonRef}
                type="button"
                onClick={() => setMenuOpen((open) => !open)}
                disabled={disabled}
                aria-haspopup="menu"
                aria-expanded={menuOpen}
                aria-label="More recording options"
                className={`${barButton} w-11 bg-white/10 px-0 text-white hover:bg-white/20`}
              >
                <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
                  <circle cx="5" cy="12" r="2" />
                  <circle cx="12" cy="12" r="2" />
                  <circle cx="19" cy="12" r="2" />
                </svg>
              </button>
            </>
          )}
        </div>

        {menuOpen && (
          <div
            ref={menuRef}
            role="menu"
            aria-label="Recording options"
            onKeyDown={onMenuKeyDown}
            className="absolute right-2 bottom-full mb-2 w-64 overflow-hidden rounded-xl bg-white py-1 text-slate-800 shadow-xl ring-1 ring-slate-200"
          >
            {linked && (
              <button
                type="button"
                role="menuitem"
                onClick={handleSomeoneJoined}
                className="a11y-touch-target block w-full px-4 py-2.5 text-left text-sm font-medium hover:bg-slate-50 focus:bg-slate-100 focus:outline-none"
              >
                Someone joined
              </button>
            )}
            {linked && (
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  setMenuOpen(false)
                  setWithdrawPreset(null)
                  setDialog('withdraw')
                }}
                className="a11y-touch-target block w-full px-4 py-2.5 text-left text-sm font-semibold text-red-700 hover:bg-red-50 focus:bg-red-50 focus:outline-none"
              >
                Stop — consent withdrawn
              </button>
            )}
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                setMenuOpen(false)
                setDialog('discard')
              }}
              className="a11y-touch-target block w-full px-4 py-2.5 text-left text-sm font-medium text-red-700 hover:bg-red-50 focus:bg-red-50 focus:outline-none"
            >
              Discard recording
            </button>
          </div>
        )}

        {!linked && live && (
          <p className="mt-1 px-1 text-xs text-amber-200">
            This recording isn’t linked to your account — you can pause or discard it.
          </p>
        )}
        {capWarning && (
          <p className="mt-1 px-1 text-xs font-semibold text-amber-200" role="status">
            5 minutes left — recording stops at 100 minutes
          </p>
        )}
        {silent && (
          <p className="mt-1 px-1 text-xs font-semibold text-amber-200" role="status">
            We can’t hear anything — check the mic
          </p>
        )}
        {recording.recorderError && (
          <p className="mt-1 px-1 text-xs font-semibold text-red-200" role="alert">
            Something went wrong with the recording. Finish to keep what was recorded.
          </p>
        )}
        {actionError && (
          <p className="mt-1 px-1 text-xs font-semibold text-red-200" role="alert">
            {actionError}
          </p>
        )}

        {/* Screen readers: status changes politely; the clock once a minute, never per second. */}
        <p className="sr-only" aria-live="polite" data-testid="rec-status-live">
          {statusText}
        </p>
        <p className="sr-only" aria-live="polite" data-testid="rec-timer-live">
          {formatRecordingMinutesSpoken(elapsed)}
        </p>
      </section>

      {dialog === 'someone_joined' && (
        <SomeoneJoinedDialog
          busy={busy === 'resuming'}
          error={actionError}
          onKeepPaused={() => setDialog(null)}
          onTheyDisagree={() => {
            setWithdrawPreset('other_person_present')
            setDialog('withdraw')
          }}
          onResume={(person) =>
            void run(async () => {
              await resumeActiveRecording({ personJoined: person })
              setDialog(null)
            }, 'Couldn’t resume — try again.')
          }
        />
      )}
      {dialog === 'withdraw' && (
        <WithdrawConsentDialog
          busy={busy === 'withdrawing'}
          initialBy={withdrawPreset}
          onCancel={() => setDialog(null)}
          onConfirm={handleWithdraw}
        />
      )}
      {dialog === 'discard' && (
        <DiscardRecordingDialog
          busy={busy === 'discarding'}
          onCancel={() => setDialog(null)}
          onConfirm={handleDiscard}
        />
      )}
    </>
  )
}
